import { Close, Key } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Button, useDataProvider, useListContext, useRefresh, useTranslate, useUnselectAll } from "react-admin";

import { PasswordResetRecipient } from "../generated/graphql";
import { ACCESS_STATUS, getAccessStatus } from "../lib/accessStatus";
import { space } from "../theme/designTokens";
import { PractitionerRecord } from "../types/practitioner";
import { CustomConfirm } from "./CustomConfirm";

type BulkTarget = {
  id: string;
  name: string;
  userId?: string;
  deactivated: boolean;
};

type OtpResult = {
  id: string;
  name: string;
  emailed: boolean;
  failed: boolean;
  skipped: boolean;
};

type Phase = "idle" | "preparing" | "confirm" | "running" | "results";

export const PractitionerBulkOtpAction = () => {
  const { selectedIds, data } = useListContext<PractitionerRecord>();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll("Practitioner");

  const [phase, setPhase] = useState<Phase>("idle");
  const [targets, setTargets] = useState<BulkTarget[]>([]);
  const [done, setDone] = useState(0);
  const [results, setResults] = useState<OtpResult[]>([]);

  const resolveRecord = async (id: string): Promise<PractitionerRecord | null> => {
    const record = data?.find(item => item.id === id);
    if (record) return record;
    try {
      // Selection can span pages and survive filter changes; ids outside the
      // current page data are fetched individually so the confirmation list
      // always names every practitioner that will be affected.
      const { data: fetched } = await dataProvider.getOne<PractitionerRecord>("Practitioner", { id });
      return fetched ?? null;
    } catch {
      return null;
    }
  };

  const prepareTargets = async () => {
    setPhase("preparing");
    const resolved: BulkTarget[] = [];

    for (const id of selectedIds) {
      const record = await resolveRecord(String(id));
      const name = record?.name || [record?.firstName, record?.lastName].filter(Boolean).join(" ") || String(id);
      const accessStatus = record ? (record.accessStatus ?? getAccessStatus(record.status, record.authStatus)) : null;
      resolved.push({
        id: String(id),
        name,
        userId: record?.userId,
        deactivated: accessStatus === ACCESS_STATUS.Deactivated,
      });
    }

    setTargets(resolved);
    setPhase("confirm");
  };

  const generateOtps = async () => {
    setPhase("running");
    setDone(0);
    const collected: OtpResult[] = [];

    for (const target of targets) {
      if (target.deactivated) {
        // Deactivated profiles cannot redeem a one-time password; mirror the
        // password tab, which refuses to generate one for them.
        collected.push({ ...target, emailed: false, failed: false, skipped: true });
      } else if (!target.userId) {
        collected.push({ ...target, emailed: false, failed: true, skipped: false });
      } else {
        try {
          const { warningCode } = await dataProvider.custom("Practitioner", "resetPasswordOTP", {
            userId: target.userId,
            recipient: PasswordResetRecipient.User,
          });
          collected.push({
            ...target,
            emailed: warningCode !== "USER_HAS_NO_EMAIL",
            failed: false,
            skipped: false,
          });
        } catch {
          collected.push({ ...target, emailed: false, failed: true, skipped: false });
        }
      }
      setDone(previous => previous + 1);
    }

    setResults(collected);
    setPhase("results");
  };

  const handleClose = () => {
    setPhase("idle");
    setResults([]);
    setTargets([]);
    unselectAll();
    refresh();
  };

  const noteFor = (result: OtpResult): string => {
    if (result.skipped) return translate("bulkOtp.skippedDeactivated");
    if (result.failed) return translate("bulkOtp.failed");
    return translate(result.emailed ? "bulkOtp.emailed" : "bulkOtp.noEmail");
  };

  const confirmContent = (
    <>
      {translate("bulkOtp.confirmContent", { smart_count: targets.length })}
      <Box component="span" sx={{ display: "block", mt: space.xs }} data-testid="bulk-otp-target-list">
        {targets.map(target => (
          <Typography key={target.id} component="span" variant="body2" sx={{ display: "block" }}>
            {target.name}
            {target.deactivated ? ` (${translate("bulkOtp.skippedDeactivated")})` : ""}
          </Typography>
        ))}
      </Box>
    </>
  );

  return (
    <>
      <Button
        label={translate("bulkOtp.button")}
        onClick={prepareTargets}
        disabled={phase !== "idle"}
        data-testid="bulk-otp-button"
      >
        <Key />
      </Button>
      <CustomConfirm
        isOpen={phase === "confirm"}
        title={translate("bulkOtp.confirmTitle")}
        content={confirmContent}
        onConfirm={generateOtps}
        onClose={() => setPhase("idle")}
      />
      <Dialog open={phase === "running" || phase === "results"} fullWidth maxWidth="sm">
        <DialogTitle>{translate("bulkOtp.resultsTitle")}</DialogTitle>
        <DialogContent>
          {phase === "running" ? (
            <>
              <Typography variant="body1" sx={{ mb: space.xs }}>
                {translate("bulkOtp.progress", { done, total: targets.length })}
              </Typography>
              <LinearProgress variant="determinate" value={targets.length ? (done / targets.length) * 100 : 0} />
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: space.xs }}>
                {translate("bulkOtp.resultsHint")}
              </Typography>
              <Table size="small" data-testid="bulk-otp-results">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("bulkOtp.colName")}</TableCell>
                    <TableCell>{translate("bulkOtp.colNote")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(result => (
                    <TableRow key={result.id}>
                      <TableCell>{result.name}</TableCell>
                      <TableCell>{noteFor(result)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        {phase === "results" && (
          <DialogActions>
            <Button label={translate("bulkOtp.close")} onClick={handleClose} data-testid="bulk-otp-close">
              <Close />
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
